import React, { forwardRef, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import FormErrorMessage from '../Errormessage';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const getLabelColor = ({ disabled, error, authInput }) => {
  if (disabled) return '#9ca3af';
  if (error) return '#B41E45';
  return authInput ? '#2E2E2E' : '#6B7280';
};

const getAsteriskColor = ({ disabled, error }) =>
  disabled ? '#9ca3af' : error ? '#B41E45' : '#D0382A';

const MessageContent = ({ messages, error }) => {
  if (messages && typeof messages === 'object')
    return <FormErrorMessage error={error} messages={messages} />;
  return (
    <span className='text-danger !font-[500] font-primaryRegular ml-1 fs-12 text-[#B41E45]'>
      {messages}
    </span>
  );
};

const renderMessages = ({ error, customError, messages }) => {
  if (error && customError) {
    return (
      <div className='flex justify-end items-center mt-1 text-[#B41E45] '>
        <ErrorOutlineIcon
          sx={{ color: '#B41E45', fontSize: '14px', marginRight: '4px', fontWeight: '500' }}
        />
        <MessageContent messages={messages} error={error} />
      </div>
    );
  }
  return (
    <div>
      <MessageContent messages={messages} error={error} />
    </div>
  );
};

// --- Normalization Helpers ---
const applyNumericRestriction = (val, { allowDecimal, allowNegative }) => {
  let nextValue = val.replace(allowDecimal ? /[^0-9.-]/g : /[^0-9-]/g, '');
  if (!allowNegative) {
    nextValue = nextValue.replace(/-/g, '');
  } else {
    nextValue = nextValue.startsWith('-')
      ? `-${nextValue.slice(1).replace(/-/g, '')}`
      : nextValue.replace(/-/g, '');
  }
  if (allowDecimal) {
    const parts = nextValue.split('.');
    if (parts.length > 2) nextValue = `${parts[0]}.${parts.slice(1).join('')}`;
  }
  return nextValue;
};

const sanitizeValue = (rawValue, restriction, numericOptions) => {
  if (restriction === 'numeric') return applyNumericRestriction(rawValue, numericOptions);
  if (restriction === 'alphabetic') return rawValue.replace(/[^A-Za-z\s]/g, '');
  if (restriction === 'alphanumeric') return rawValue.replace(/[^A-Za-z0-9\s]/g, '');
  return rawValue;
};

const InputBox = forwardRef(
  (
    {
      label,
      icon,
      preIcon,
      className,
      messages,
      customError = false,
      error,
      onChange,
      name,
      type = 'text',
      placeholder,
      value,
      authInput = false,
      disabled,
      onCopy,
      onPaste,
      isEdit = false,
      required = false,
      hidelabel = false,
      capitalized = false,
      maxLength,
      inputMode,
      skipDefaultNormalization = false,
      inputRestriction,
      allowNegative = false,
      allowDecimal = false,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);

    const handleTrimmedChange = (e) => {
      let inputValue = e.target.value;
      if (!skipDefaultNormalization) {
        inputValue = inputValue.replace(/^\s+/, '').replace(/\s+/g, ' ');
      }

      const restriction = inputRestriction || (type === 'number' ? 'numeric' : null);
      inputValue = sanitizeValue(inputValue, restriction, { allowDecimal, allowNegative });

      if (type === 'email') inputValue = inputValue.toLowerCase();
      if (capitalized && type === 'text') {
        inputValue = inputValue.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
      }
      if (type === 'number' && maxLength && inputValue.length > maxLength) {
        inputValue = inputValue.slice(0, maxLength);
      }

      if (e.target.value !== inputValue) e.target.value = inputValue;
      onChange({ target: { name, value: inputValue } });
    };

    const shouldShrinkLabel = isEdit || value || focused || !!error;
    const commonCursor = disabled ? 'not-allowed' : 'text';

    return (
      <div style={{ cursor: disabled ? 'not-allowed' : 'default' }}>
        <TextField
          variant='outlined'
          label={
            hidelabel ? null : (
              <span style={{ color: getLabelColor({ disabled, error, authInput }) }}>
                {label}{' '}
                {required && (
                  <span
                    style={{
                      color: getAsteriskColor({ disabled, error }),
                      marginLeft: '-3px',
                      fontFamily: 'Primary_Regular',
                      fontWeight: '500',
                    }}
                  >
                    *
                  </span>
                )}
              </span>
            )
          }
          disabled={disabled}
          onCopy={onCopy}
          onPaste={onPaste}
          value={value}
          name={name}
          type={type}
          inputRef={ref}
          multiline={type === 'textarea'}
          rows={type === 'textarea' ? 4 : 1}
          onChange={handleTrimmedChange}
          className={`${className} asb_input_wrappper ${disabled ? 'cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          inputMode={inputMode}
          onFocus={disabled ? undefined : () => setFocused(true)}
          onBlur={disabled ? undefined : () => setFocused(false)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: authInput ? '6px' : '10px',
              backgroundColor: disabled ? '#f5f5f5' : '#fff',
              border: error
                ? '1px solid #B41E45'
                : authInput
                  ? '1px solid #645858'
                  : '1px solid #D9D9D9',
              boxShadow: 'none',
              cursor: disabled ? 'not-allowed' : 'default',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
              '&:hover': { cursor: commonCursor },
              '& .MuiInputBase-input': {
                fontWeight: '500',
                fontFamily: 'Primary_Regular',
                color: disabled ? '#9ca3af' : '#2E2E2E',
                cursor: commonCursor,
                '&::placeholder': {
                  fontSize: '14px',
                  fontFamily: 'Primary_Regular',
                  opacity: disabled ? 0.3 : 0.5,
                  color: disabled ? '#9ca3af' : 'inherit',
                },
              },
            },
            '& input:-webkit-autofill': {
              backgroundColor: disabled ? '#f5f5f5 !important' : '#fff !important',
              color: disabled ? '#9ca3af !important' : '#2E2E2E !important',
            },
            '& .MuiInputAdornment-root': {
              backgroundColor: disabled ? '#f5f5f5' : '#fff',
            },
          }}
          InputProps={{
            style: { cursor: commonCursor },
            startAdornment: preIcon && (
              <InputAdornment
                position='start'
                sx={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: disabled ? '#f5f5f5' : '#fff',
                  opacity: disabled ? 0.5 : 1,
                  '& *': { color: disabled ? '#9ca3af !important' : 'inherit' },
                }}
              >
                {preIcon}
              </InputAdornment>
            ),
            endAdornment: icon && (
              <InputAdornment
                position='end'
                sx={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: disabled ? '#f5f5f5' : '#fff',
                  opacity: disabled ? 0.5 : 1,
                  '& *': { color: disabled ? '#9ca3af !important' : 'inherit' },
                }}
              >
                {icon}
              </InputAdornment>
            ),
            inputProps: { maxLength, style: { cursor: commonCursor }, ...props.inputProps },
          }}
          InputLabelProps={{
            shrink: shouldShrinkLabel,
            style: {
              color: disabled ? '#9ca3af' : authInput ? '#2E2E2E' : '#6B7280',
              backgroundColor: disabled ? '#f5f5f5' : '#FFFFFF',
              paddingLeft: '2px',
              paddingRight: '2px',
              fontFamily: 'Futura',
              fontSize: authInput && shouldShrinkLabel ? '20px' : '18px',
              fontWeight: '500',
              lineHeight: '26px',
              ...(type === 'textarea' &&
                !shouldShrinkLabel && {
                  left: 15,
                  top: '50%',
                  transform: 'translate(0, -50%)',
                  fontSize: '18px',
                  color: disabled ? '#9ca3af' : '#6B7280',
                  fontFamily: 'Futura',
                  fontWeight: '500',
                  backgroundColor: disabled ? '#f5f5f5' : '#FFFFFF',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  paddingTop: '0',
                  paddingBottom: '0',
                  pointerEvents: 'none',
                }),
            },
          }}
          {...props}
        />
        {renderMessages({ error, customError, messages })}
      </div>
    );
  }
);

export default InputBox;
